import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Building2, User, Phone, Mail, Loader2, FileText, Wallet, CheckCircle2, Lock, Unlock, Upload } from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro, fmtDate, STATUS, formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";

function ContactDocuments({ contactId }) {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setCode(""); setUnlocked(false); setDocs([]); }, [contactId]);

  const loadDocs = async (c) => {
    try {
      const r = await api.get(`/contacts/${contactId}/documents`, { headers: { "X-Docs-Code": c } });
      setDocs(r.data); setUnlocked(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setUnlocked(false); }
  };
  const unlock = async () => {
    if (!code) { toast.error("Inserisci il codice"); return; }
    try {
      await api.post(`/contacts/${contactId}/documents/verify`, { code });
      await loadDocs(code);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/contacts/${contactId}/documents`, fd, { headers: { "X-Docs-Code": code, "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      await loadDocs(code);
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const openDoc = async (d) => {
    try {
      const r = await api.get(`/documents/${d.id}/download`, { headers: { "X-Docs-Code": code }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Impossibile aprire il documento"); }
  };
  const removeDoc = async (d) => {
    try { await api.delete(`/documents/${d.id}`, { headers: { "X-Docs-Code": code } }); toast.success("Documento eliminato"); await loadDocs(code); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-900/60 dark:bg-amber-950/20 p-4" data-testid="contact-documents">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-4 h-4 text-amber-600" />
        <div className="text-sm font-semibold">Documenti riservati (GDPR)</div>
      </div>
      {!unlocked ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Accesso protetto. Inserisci il codice impostato nelle Impostazioni per vedere i documenti di questo cliente.</p>
          <div className="flex gap-2">
            <Input type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Codice documenti" onKeyDown={(e) => e.key === "Enter" && unlock()} data-testid="doc-code-input" />
            <Button onClick={unlock} data-testid="doc-unlock-button"><Unlock className="w-4 h-4 mr-1.5" /> Sblocca</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="rounded-lg border border-border bg-card divide-y divide-border">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`doc-row-${d.id}`}>
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <button className="flex-1 truncate text-left hover:underline" onClick={() => openDoc(d)} data-testid={`doc-open-${d.id}`}>{d.original_filename}</button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeDoc(d)} data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            {docs.length === 0 && <div className="px-3 py-4 text-center text-xs text-muted-foreground">Nessun documento caricato.</div>}
          </div>
          <input ref={fileRef} type="file" accept={DOC_ACCEPT} onChange={upload} className="hidden" data-testid="doc-file-input" />
          <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-button">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Carica documento (PDF/foto)
          </Button>
        </div>
      )}
    </div>
  );
}

const EMPTY = { ragione_sociale: "", tipo: "cliente", natura: "azienda", referente: "", email: "", telefono: "", piva: "", indirizzo: "", note: "" };

const TIPI = {
  cliente: { label: "Cliente", cls: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800" },
  collaboratore: { label: "Collaboratore", cls: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/50 dark:text-violet-300 dark:border-violet-800" },
  fornitore: { label: "Fornitore", cls: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800" },
};

const NATURA = {
  azienda: { label: "Azienda", icon: Building2, docLabel: "P.IVA" },
  privato: { label: "Privato", icon: User, docLabel: "Codice Fiscale" },
};

export default function Contatti() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tipoFilter, setTipoFilter] = useState("tutti");
  const [sortMode, setSortMode] = useState("alfabetico");
  const [params, setParams] = useSearchParams();
  const detailId = params.get("cliente");

  const load = () => Promise.all([cachedGet("/contacts"), cachedGet("/quotes")]).then(([c, qs]) => {
    setContacts(c.data); setQuotes(qs.data);
  });
  useEffect(() => { load(); }, []);

  const quotesOf = (c) => quotes.filter((x) => x.contact_id === c.id || x.cliente_nome === c.ragione_sociale);
  const spesoOf = (c) => quotesOf(c).filter((x) => x.stato === "accettato").reduce((s, x) => s + (x.totale || 0), 0);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c, natura: c.natura || "azienda" }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.ragione_sociale) { toast.error(form.natura === "privato" ? "Inserisci nome e cognome" : "Inserisci la ragione sociale"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/contacts/${editId}`, form);
      else await api.post("/contacts", form);
      toast.success(editId ? "Contatto aggiornato" : "Contatto aggiunto");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    await api.delete(`/contacts/${toDelete.id}`);
    toast.success("Contatto eliminato");
    setToDelete(null); load();
  };

  const filtered = contacts.filter((c) => {
    const mq = !q || [c.ragione_sociale, c.referente, c.piva, c.email].join(" ").toLowerCase().includes(q.toLowerCase());
    const mt = tipoFilter === "tutti" || (c.tipo || "cliente") === tipoFilter;
    return mq && mt;
  });

  const ordered = sortMode === "alfabetico"
    ? [...filtered].sort((a, b) => (a.ragione_sociale || "").localeCompare(b.ragione_sociale || "", "it", { sensitivity: "base" }))
    : filtered;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const natura = form.natura || "azienda";
  const docLabel = NATURA[natura].docLabel;

  const detail = detailId ? contacts.find((c) => c.id === detailId) || null : null;
  const openDetail = (c) => setParams((p) => { const n = new URLSearchParams(p); n.set("cliente", c.id); return n; });
  const closeDetail = () => setParams((p) => { const n = new URLSearchParams(p); n.delete("cliente"); return n; });

  return (
    <div className="space-y-5">
      {!isAdmin && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300 px-4 py-2.5 text-sm" data-testid="contacts-staff-notice">
          Modalità staff: puoi consultare l'elenco clienti; i dati sensibili (P.IVA, contatti) e la modifica sono riservati all'amministratore.
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Cerca per nome, P.IVA, email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="contacts-search-input" />
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-auto">
          <Select value={sortMode} onValueChange={setSortMode}>
            <SelectTrigger className="w-40" data-testid="contacts-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alfabetico">Ordine A → Z</SelectItem>
              <SelectItem value="recenti">Più recenti</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && <Button onClick={openNew} data-testid="contact-add-button"><Plus className="w-4 h-4 mr-1.5" /> Nuovo Contatto</Button>}
        </div>
      </div>

      <Tabs value={tipoFilter} onValueChange={setTipoFilter}>
        <TabsList data-testid="contacts-tipo-tabs">
          <TabsTrigger value="tutti">Tutti</TabsTrigger>
          <TabsTrigger value="cliente">Clienti</TabsTrigger>
          <TabsTrigger value="collaboratore">Collaboratori</TabsTrigger>
          <TabsTrigger value="fornitore">Fornitori</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="contacts-table">
        {ordered.map((c) => {
          const nat = NATURA[c.natura || "azienda"];
          const NatIcon = nat.icon;
          return (
            <div key={c.id} onClick={() => openDetail(c)} className="rounded-xl border border-border bg-card p-5 hover:shadow-md hover:border-primary/40 transition-all group cursor-pointer" data-testid={`contact-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <NatIcon className="w-5 h-5" />
                </div>
                {isAdmin && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openEdit(c); }} data-testid={`contact-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setToDelete(c); }} data-testid={`contact-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="font-heading font-semibold leading-tight">{c.ragione_sociale}</span>
                <Badge variant="outline" className={`${TIPI[c.tipo || "cliente"]?.cls} text-[10px]`}>{TIPI[c.tipo || "cliente"]?.label}</Badge>
                <Badge variant="outline" className="text-[10px]">{nat.label}</Badge>
              </div>
              {c.referente && <div className="text-sm text-muted-foreground">{c.referente}</div>}
              <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                {c.telefono && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {c.telefono}</div>}
                {c.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 shrink-0" /> {c.email}</div>}
                {c.indirizzo && <div className="flex items-start gap-2"><Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {c.indirizzo}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{quotesOf(c).length} preventivi</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">{euro(spesoOf(c))} accettati</span>
              </div>
            </div>
          );
        })}
        {ordered.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">Nessun contatto trovato.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="contact-dialog" onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT" && !saving) { e.preventDefault(); save(); } }}>
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifica contatto" : "Nuovo contatto"}</DialogTitle><DialogDescription className="sr-only">Inserisci o modifica i dati anagrafici del contatto.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label>Natura contatto</Label>
              <Tabs value={natura} onValueChange={(n) => setForm((f) => ({ ...f, natura: n }))}>
                <TabsList className="grid w-full grid-cols-2" data-testid="contact-natura-tabs">
                  <TabsTrigger value="azienda" data-testid="contact-natura-azienda">Azienda (P.IVA)</TabsTrigger>
                  <TabsTrigger value="privato" data-testid="contact-natura-privato">Privato</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>{natura === "privato" ? "Nome e Cognome *" : "Ragione Sociale *"}</Label>
              <Input value={form.ragione_sociale} onChange={set("ragione_sociale")} data-testid="contact-name-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Tipo contatto</Label>
              <Select value={form.tipo || "cliente"} onValueChange={(t) => setForm((f) => ({ ...f, tipo: t }))}>
                <SelectTrigger data-testid="contact-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cliente">Cliente</SelectItem>
                  <SelectItem value="collaboratore">Collaboratore</SelectItem>
                  <SelectItem value="fornitore">Fornitore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Referente</Label><Input value={form.referente} onChange={set("referente")} /></div>
            <div className="space-y-2"><Label>{docLabel}</Label><Input value={form.piva} onChange={set("piva")} data-testid="contact-piva-input" placeholder={natura === "privato" ? "RSSMRA80A01L…" : "IT01234567890"} /></div>
            <div className="space-y-2"><Label>Telefono</Label><Input value={form.telefono} onChange={set("telefono")} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={set("email")} /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={set("indirizzo")} /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Note</Label><Textarea value={form.note} onChange={set("note")} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="contact-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 flex-wrap">
                  {detail.ragione_sociale}
                  <Badge variant="outline" className={TIPI[detail.tipo || "cliente"]?.cls}>{TIPI[detail.tipo || "cliente"]?.label}</Badge>
                  <Badge variant="outline">{NATURA[detail.natura || "azienda"].label}</Badge>
                </DialogTitle>
                <DialogDescription className="sr-only">Scheda cliente con storico preventivi e totale speso.</DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  {detail.referente && <div><span className="text-muted-foreground">Referente: </span>{detail.referente}</div>}
                  {detail.piva && <div><span className="text-muted-foreground">{NATURA[detail.natura || "azienda"].docLabel}: </span>{detail.piva}</div>}
                  {detail.telefono && <div><span className="text-muted-foreground">Tel: </span>{detail.telefono}</div>}
                  {detail.email && <div><span className="text-muted-foreground">Email: </span>{detail.email}</div>}
                  {detail.indirizzo && <div className="sm:col-span-2"><span className="text-muted-foreground">Indirizzo: </span>{detail.indirizzo}</div>}
                  {detail.note && <div className="sm:col-span-2"><span className="text-muted-foreground">Note: </span>{detail.note}</div>}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border border-border p-3 text-center">
                    <FileText className="w-4 h-4 mx-auto text-primary mb-1" />
                    <div className="font-heading text-xl font-bold">{quotesOf(detail).length}</div>
                    <div className="text-xs text-muted-foreground">Preventivi</div>
                  </div>
                  <div className="rounded-lg border border-border p-3 text-center">
                    <CheckCircle2 className="w-4 h-4 mx-auto text-emerald-500 mb-1" />
                    <div className="font-heading text-xl font-bold">{quotesOf(detail).filter((x) => x.stato === "accettato").length}</div>
                    <div className="text-xs text-muted-foreground">Accettati</div>
                  </div>
                  <div className="rounded-lg border border-border p-3 text-center">
                    <Wallet className="w-4 h-4 mx-auto text-primary mb-1" />
                    <div className="font-heading text-xl font-bold">{euro(spesoOf(detail))}</div>
                    <div className="text-xs text-muted-foreground">Totale speso</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-semibold mb-2">Storico preventivi</div>
                  <div className="rounded-lg border border-border divide-y divide-border">
                    {quotesOf(detail).map((x) => (
                      <div key={x.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                        <span className="font-mono text-xs text-muted-foreground w-14">N.{x.numero}</span>
                        <span className="flex-1 truncate">{x.oggetto || "—"}</span>
                        <span className="text-muted-foreground text-xs hidden sm:inline">{fmtDate(x.data_emissione)}</span>
                        <span className="font-semibold">{euro(x.totale)}</span>
                        <Badge variant="outline" className={STATUS[x.stato]?.cls}>{STATUS[x.stato]?.label}</Badge>
                      </div>
                    ))}
                    {quotesOf(detail).length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Nessun preventivo per questo cliente.</div>}
                  </div>
                </div>

                {isAdmin && <ContactDocuments contactId={detail.id} />}

                {isAdmin && (
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => { openEdit(detail); closeDetail(); }}><Pencil className="w-4 h-4 mr-1.5" /> Modifica dati</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il contatto?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.ragione_sociale} sarà rimosso dalla rubrica.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-contact">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
